import { Link } from "react-router-dom";
import {
  Building2,
  ChevronRight,
  ExternalLink,
  FileText,
  Flag,
  Inbox,
  Lock,
  User,
} from "lucide-react";
import { cn } from "@/lib/cn";
import { clientRoutes } from "@/lib/clientRoutes";
import type { TaskListItem } from "@/api/types";

interface Props {
  task: TaskListItem;
  showAssignee?: boolean;
  done?: boolean;
  onOpen: () => void;
  /** v1.2/§20 — opens the canonical TaskCompleteModal (NO one-click complete). */
  onComplete?: () => void;
  /** FS-3 (§0/§18.2): own-ness gate. Backend Task ACL = edit:own, so the row
   *  complete-CTA on a team (non-own) task would 403 → dead-end. Hide it for
   *  non-own rows (the row still opens the read-only detail drawer). Default true
   *  preserves every own-tab call site untouched. UI-only — no ACL change. */
  isOwn?: boolean;
}

const PRIORITY_TONE: Record<string, string> = {
  high: "text-destructive bg-destructive/10 border-destructive/20",
  medium: "text-warning-foreground bg-warning border-warning-foreground/20",
  low: "text-muted-foreground border-border",
};

const BUCKET_TONE: Record<string, string> = {
  overdue: "text-destructive font-medium",
  today: "text-warning-foreground font-medium",
  week: "text-primary",
  later: "text-muted-foreground",
};

/** v1.2 — subtype chip (VISUAL: 4 outcome-families). */
const SUBTYPE_CHIP: Record<string, { label: string; cls: string }> = {
  generic: { label: "завдання", cls: "bg-muted text-muted-foreground" },
  call: { label: "дзвінок", cls: "bg-info text-info-foreground" },
  follow_up: { label: "follow-up", cls: "bg-success text-success-foreground" },
  bi_signal: { label: "BI-сигнал", cls: "bg-violet text-violet-foreground" },
};

export function TaskRow({
  task,
  showAssignee,
  done,
  onOpen,
  onComplete,
  isOwn = true,
}: Props) {
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen();
        }
      }}
      className="group flex min-h-[72px] items-center gap-3 px-3.5 py-3 rounded-lg border bg-background hover:border-primary/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring transition-colors cursor-pointer"
    >
      {/* status dot — VISUAL only; completion goes through the outcome modal (§20 no one-click) */}
      <span
        aria-hidden
        className={cn(
          "w-4.5 h-4.5 shrink-0 rounded-full border-2 grid place-items-center",
          done ? "border-primary bg-primary" : "border-muted-foreground/40",
        )}
      >
        {done && (
          <svg
            viewBox="0 0 16 16"
            className="w-2.5 h-2.5 text-primary-foreground"
            fill="none"
            stroke="currentColor"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M3 8.5l3 3 7-7" />
          </svg>
        )}
      </span>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 min-w-0">
          <span
            className={cn(
              "text-sm font-medium truncate",
              done && "line-through text-muted-foreground",
            )}
          >
            {task.title}
          </span>
          {task.subtype && task.subtype !== "generic" && SUBTYPE_CHIP[task.subtype] && (
            <span
              className={cn(
                "shrink-0 rounded px-1.5 py-0.5 text-[10px] font-semibold",
                SUBTYPE_CHIP[task.subtype].cls,
              )}
            >
              {SUBTYPE_CHIP[task.subtype].label}
            </span>
          )}
        </div>
        <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
          {task.clientAccessLocked ? (
            /* D-142.W1 (П-3) — watcher без доступу до клієнта: чесний lock-лейбл,
               НЕ «Загальна» (задача клієнтська, але клієнт зредагований). */
            <span className="inline-flex items-center gap-1">
              <Lock className="w-3 h-3" aria-hidden /> Клієнт недоступний
            </span>
          ) : task.accountId && task.accountName ? (
            <Link
              to={clientRoutes.accountAnchor(task.accountId)}
              onClick={(e) => e.stopPropagation()}
              className="inline-flex items-center gap-1 hover:text-primary"
            >
              <Building2 className="w-3 h-3" aria-hidden />
              <span className="truncate max-w-[200px]">{task.accountName}</span>
            </Link>
          ) : task.holdingId ? (
            /* holding-parent task (3A create) — link straight to Client360 */
            <Link
              to={clientRoutes.client(task.holdingId)}
              onClick={(e) => e.stopPropagation()}
              className="inline-flex items-center gap-1 hover:text-primary"
            >
              <Building2 className="w-3 h-3" aria-hidden />
              <span className="truncate max-w-[200px]">{task.holdingName ?? "Картка клієнта"}</span>
            </Link>
          ) : (
            <span className="inline-flex items-center gap-1">
              <Inbox className="w-3 h-3" aria-hidden /> Загальна
            </span>
          )}
          {task.source === "meeting" && task.accountId && task.sourceLabel && (
            <Link
              to={clientRoutes.accountAnchor(task.accountId, "activities")}
              onClick={(e) => e.stopPropagation()}
              className="inline-flex items-center gap-1 hover:text-primary"
            >
              <FileText className="w-3 h-3" aria-hidden />
              <span>{task.sourceLabel}</span>
              <ExternalLink className="w-2.5 h-2.5" aria-hidden />
            </Link>
          )}
          {showAssignee && task.assigneeName && (
            <span className="inline-flex items-center gap-1">
              <User className="w-3 h-3" aria-hidden />
              <span className="truncate max-w-[160px]">{task.assigneeName}</span>
            </span>
          )}
        </div>
      </div>

      {!done && (
        <div className="flex items-center gap-2 shrink-0">
          {task.priorityReason ? (
            <span
              className={cn(
                "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[12px] font-medium",
                PRIORITY_TONE[task.priority],
              )}
            >
              <Flag className="w-2.5 h-2.5" aria-hidden />
              <span className="truncate max-w-[120px]">{task.priorityReason}</span>
            </span>
          ) : (
            task.priority === "high" && (
              <Flag className="w-3.5 h-3.5 text-destructive" aria-hidden />
            )
          )}
          <span className={cn("text-xs whitespace-nowrap", BUCKET_TONE[task.bucket])}>
            {task.deadlineLabel}
          </span>
          {/* row-CTA → canonical TaskCompleteModal (stopPropagation; row-click → detail).
              FS-3: hidden for non-own (team) tasks — edit:own would 403. */}
          {onComplete && isOwn && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onComplete();
              }}
              className="rounded-md bg-primary px-2.5 py-1.5 text-xs font-semibold text-primary-foreground hover:bg-primary/90"
            >
              Завершити
            </button>
          )}
          <ChevronRight className="w-3.5 h-3.5 text-muted-foreground/50" aria-hidden />
        </div>
      )}
    </div>
  );
}
